import { useEffect, useRef, forwardRef } from 'react';
import { cn } from '@/utils';

const SelectBox = forwardRef(({
  headline,
  children,
  onClick,
  onKeyDown,
  onBlur,
  linkText,
  onLinkClick,
  isExpanded,
  showIcon,
  DropdownContent,
  onClickOutside,
  isDisabled,
  disabledClassName,
  id,
  isHighlighted,
  highlightedClassName,
  testid,
  buttonDataAttributes = {}
}, ref) => {
  const parentRef = useRef(null);

  const handleClickOutside = (e) => {
    if (parentRef.current && !parentRef.current.contains(e.target)) {
      onClickOutside();
    }
  };

  const handleLinkClick = () => {
    onLinkClick();
  };

  useEffect(() => {
    if (onClickOutside && isExpanded) {
      document.addEventListener('click', handleClickOutside, { once: true, capture: true });
    }
  }, [isExpanded]);

  return (
    <div id={id} className="mb-2xl" ref={ref}>
      <div className="flex justify-between items-center mb-2xs">
        <label
          id={`listbox-${id}-label`}
          className="block font-semibold text-sm desktop:text-base"
        >
          {headline}
        </label>
        <If condition={linkText}>
          <button
            className="bg-transparent border-none text-[color:var(--theme-link-text-color-2)] font-bold inline-flex items-center whitespace-nowrap gap-4xs"
            onClick={handleLinkClick}
          >
            <ft-icon name="info" width={20} height={20}></ft-icon>
            <span className="text-xs desktop:text-sm">{linkText}</span>
          </button>
        </If>
      </div>
      <div ref={parentRef} className="relative mt-5xs">
        <button
          data-testid={testid}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={(isExpanded && DropdownContent) ? "true" : "false"}
          aria-describedby={`listbox-${id}-label`}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onBlur={({ relatedTarget }) => {
            if (relatedTarget && parentRef.current && !parentRef.current.contains(relatedTarget)) {
              onClickOutside();
            }
          }}
          className={cn(
            'relative py-5xs pl-lg pr-5xl w-full h-[4.375rem] font-semibold text-sm bg-white border rounded',
            'desktop:text-base desktop:pr-6xl',
            isDisabled ? disabledClassName : '',
            isHighlighted ? highlightedClassName : 'border-neutral-darker',
            !showIcon || isDisabled ? 'cursor-default' : ''
          )}
          {...buttonDataAttributes}
        >
          {children}
          <If condition={showIcon}>
            <span className="flex absolute inset-y-0 right-0 items-center ml-2xs pointer-events-none pr-3xs desktop:pr-sm">
              <ft-icon name="caret-down" color="brand" height="24"></ft-icon>
            </span>
          </If>
        </button>
        <If condition={isExpanded && DropdownContent}>{DropdownContent}</If>
      </div>
    </div>
  );
});

export default SelectBox;
